// components/lead-generation/business-types/ESGLeadDetailsModal.js

import React, { useState } from 'react';

export default function ESGLeadDetailsModal({ lead, onClose, onAddToCustomers }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddingToCustomers, setIsAddingToCustomers] = useState(false);
  const [notes, setNotes] = useState('');

  const getComplianceColor = (requirement) => {
    switch (requirement) {
      case 'CSRD_MANDATORY':
        return 'text-red-700 bg-red-100 border-red-200';
      case 'VSME_VOLUNTARY':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'CRITICAL':
        return 'text-red-600 bg-red-100';
      case 'HIGH':
        return 'text-orange-600 bg-orange-100';
      case 'MEDIUM':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    if (amount >= 1000000) return `€${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `€${(amount / 1000).toFixed(0)}K`;
    return `€${amount}`;
  };

  const handleAddToCustomers = async () => {
    setIsAddingToCustomers(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (onAddToCustomers) {
        onAddToCustomers(lead, notes);
      }
      onClose();
    } catch (error) {
      console.error('Error adding to customers:', error);
    } finally {
      setIsAddingToCustomers(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'ESG Overview', icon: 'fas fa-leaf' },
    { id: 'compliance', name: 'Compliance', icon: 'fas fa-gavel' },
    { id: 'business', name: 'Business Data', icon: 'fas fa-chart-bar' },
    { id: 'insights', name: 'AI Insights', icon: 'fas fa-brain' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
              <i className="fas fa-leaf text-white text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{lead.name}</h2>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm text-gray-600">{lead.industry}</span>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-600">{lead.location}</span>
                {lead.employees && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="text-sm text-gray-600">{lead.employees} employees</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className={`px-3 py-2 rounded-lg border font-semibold text-sm ${getPriorityColor(lead.priority)}`}>
              <i className="fas fa-star mr-1" />
              {lead.totalScore}% • {lead.priority}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-all"
            >
              <i className="fas fa-times text-lg" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 bg-white">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-all ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={tab.icon} />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 max-h-[500px] overflow-y-auto bg-gray-50">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* ESG Compliance Status */}
              <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="fas fa-leaf text-green-500 mr-2" />
                  ESG Compliance Status
                </h4>
                <div className={`p-4 rounded-lg border ${getComplianceColor(lead.esgAssessment?.primaryRequirement)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">
                      {lead.esgAssessment?.primaryRequirement === 'CSRD_MANDATORY' ? 'CSRD Mandatory' :
                       lead.esgAssessment?.primaryRequirement === 'VSME_VOLUNTARY' ? 'VSME Eligible' : 'Future Preparation'}
                    </span>
                    {lead.esgAssessment?.csrd?.deadline && (
                      <span className="text-sm font-medium">
                        Deadline: {lead.esgAssessment.csrd.deadline}
                      </span>
                    )}
                  </div>
                  <p className="text-sm">
                    {lead.esgAssessment?.reasoning?.[0] || 'ESG compliance assessment completed'}
                  </p>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-blue-200 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-blue-600">{lead.employees || 'N/A'}</div>
                  <div className="text-xs text-blue-600 font-medium uppercase tracking-wide">Employees</div>
                </div>
                <div className="bg-white border border-green-200 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(lead.turnover)}</div>
                  <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Turnover</div>
                </div>
                <div className="bg-white border border-purple-200 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-purple-600">{lead.totalScore}%</div>
                  <div className="text-xs text-purple-600 font-medium uppercase tracking-wide">Lead Score</div>
                </div>
                <div className="bg-white border border-orange-200 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-orange-600">
                    {formatCurrency(lead.esgAssessment?.businessValue?.estimated)}
                  </div>
                  <div className="text-xs text-orange-600 font-medium uppercase tracking-wide">Opportunity</div>
                </div>
              </div>

              {/* Company Description */}
              <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="fas fa-building text-gray-500 mr-2" />
                  Company Overview
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  {lead.description || `${lead.name} is a ${lead.industry?.toLowerCase()} company based in ${lead.location}${lead.employees ? ` with ${lead.employees} employees` : ''}. They represent a strong ESG compliance opportunity based on our analysis.`}
                </p>
              </div>
            </div>
          )}

          {/* Compliance Tab */}
          {activeTab === 'compliance' && (
            <div className="space-y-6">
              {/* VSME Assessment */}
              {lead.esgAssessment?.vsme && (
                <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="fas fa-clipboard-check text-yellow-500 mr-2" />
                    VSME Eligibility Assessment
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">VSME Eligible:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.esgAssessment.vsme.isEligible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {lead.esgAssessment.vsme.isEligible ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Qualification Score: {lead.esgAssessment.vsme.qualificationScore?.toFixed(0)}%
                    </div>
                  </div>
                </div>
              )}

              {/* CSRD Assessment */}
              {lead.esgAssessment?.csrd && (
                <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="fas fa-gavel text-red-500 mr-2" />
                    CSRD Compliance Assessment
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">CSRD Mandatory:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.esgAssessment.csrd.isMandatory ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {lead.esgAssessment.csrd.isMandatory ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {lead.esgAssessment.csrd.deadline && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Reporting Deadline:</span>
                        <span className="text-sm font-medium text-red-600">
                          {lead.esgAssessment.csrd.deadline}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Urgency Level:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.esgAssessment.csrd.urgency === 'IMMEDIATE' ? 'bg-red-100 text-red-700' :
                        lead.esgAssessment.csrd.urgency === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {lead.esgAssessment.csrd.urgency}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Industry Risk */}
              {lead.esgAssessment?.industryRisk && (
                <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="fas fa-industry text-purple-500 mr-2" />
                    Industry ESG Risk Assessment
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Risk Level:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.esgAssessment.industryRisk.level === 'HIGH' ? 'bg-red-100 text-red-700' :
                        lead.esgAssessment.industryRisk.level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {lead.esgAssessment.industryRisk.level}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Industry: {lead.esgAssessment.industryRisk.category}
                    </div>
                    <div className="text-sm text-gray-600">
                      {lead.esgAssessment.industryRisk.reasoning}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Business Data Tab */}
          {activeTab === 'business' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-lg font-bold text-gray-900">{lead.employees || 'N/A'}</div>
                  <div className="text-sm text-gray-600">Employees</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(lead.turnover)}</div>
                  <div className="text-sm text-gray-600">Annual Turnover</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(lead.balanceSheet)}</div>
                  <div className="text-sm text-gray-600">Balance Sheet</div>
                </div>
              </div>
            </div>
          )}

          {/* Insights Tab */}
          {activeTab === 'insights' && (
            <div className="space-y-6">
              {lead.esgInsights && lead.esgInsights.length > 0 ? (
                lead.esgInsights.map((insight, index) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                    <h5 className="font-medium text-gray-900 mb-2">{insight.title}</h5>
                    <p className="text-sm text-gray-700 mb-3">{insight.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Action: {insight.actionRequired}</span>
                      <span className={`px-2 py-1 rounded-full font-medium ${
                        insight.urgency === 'HIGH' ? 'bg-red-100 text-red-700' :
                        insight.urgency === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {insight.urgency}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <i className="fas fa-brain text-4xl mb-4" />
                  <p>No AI insights generated yet</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <i className="fas fa-database text-gray-400" />
              <span>Source: {lead.dataSource}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleAddToCustomers}
              disabled={isAddingToCustomers}
              className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-400 transition-all"
            >
              {isAddingToCustomers ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus mr-2" />
                  Add to Customers
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}